import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, IconButton, Link, Paper, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { CheckCircle as CheckIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { requestLookupPath } from '../services/requestLookupApi';

interface PlatformRequestIdProps {
  requestId?: string | null;
  /** Set when an admin is viewing another customer's run, so the lookup searches that account. */
  customerId?: string | null;
  sx?: SxProps<Theme>;
}

/** The Passio platform request id a run was made for, with a link to every run for that request. */
export const PlatformRequestId: React.FC<PlatformRequestIdProps> = ({ requestId, customerId, sx }) => {
  const [copied, setCopied] = useState(false);
  if (!requestId) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(requestId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Box sx={sx}>
      <Paper variant="outlined" sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Platform request ID
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
          {requestId}
        </Typography>
        <Tooltip title={copied ? 'Copied' : 'Copy request ID'}>
          <IconButton size="small" onClick={copy} aria-label="Copy platform request ID">
            {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Link component={RouterLink} to={requestLookupPath(requestId, customerId)} variant="body2" underline="hover" sx={{ ml: 'auto' }}>
          All runs for this request
        </Link>
      </Paper>
    </Box>
  );
};
